import { authFetch } from './AuthService';

// Derive the setId from a card id like "sv3pt5-25" — setIds can themselves
// contain hyphens, so split on the LAST one.
const setIdFromCardId = (cardId) => cardId.slice(0, cardId.lastIndexOf('-'));

// Build the snapshot payload the server stores alongside a wishlist row,
// from a full card object (as held by CardModal / CardGrid).
export const cardSnapshot = (card) => ({
  cardName: card.name || '',
  cardImage: card.images?.small || '',
  setId: card.set?.id || setIdFromCardId(card.id),
  setName: card.set?.name || '',
  cardNumber: String(card.number ?? ''),
  rarity: card.rarity || null
});

export async function getWishlist() {
  const res = await authFetch('/api/wishlist');
  if (!res.ok) throw new Error('Failed to load wishlist');
  const data = await res.json();
  return data.items;
}

// Wishlist is per-variant — wanting the Reverse Holo of a card you already
// own the Regular of needs its own row.
export async function addToWishlist(card, variant, priority = 'medium') {
  const res = await authFetch(
    `/api/wishlist/${encodeURIComponent(card.id)}/${encodeURIComponent(variant)}`,
    { method: 'PUT', body: JSON.stringify({ priority, ...cardSnapshot(card) }) }
  );
  if (!res.ok) throw new Error('Failed to add to wishlist');
  const data = await res.json();
  return data.item;
}

export async function setWishlistPriority(item, priority) {
  const res = await authFetch(
    `/api/wishlist/${encodeURIComponent(item.cardId)}/${encodeURIComponent(item.variant)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        priority,
        cardName: item.cardName,
        cardImage: item.cardImage,
        setId: item.setId,
        setName: item.setName,
        cardNumber: item.cardNumber,
        rarity: item.rarity
      })
    }
  );
  if (!res.ok) throw new Error('Failed to update priority');
  const data = await res.json();
  return data.item;
}

export async function removeFromWishlist(cardId, variant) {
  const res = await authFetch(
    `/api/wishlist/${encodeURIComponent(cardId)}/${encodeURIComponent(variant)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error('Failed to remove from wishlist');
  return res.json();
}

// ---- Friends ----

export async function getFriends() {
  const res = await authFetch('/api/friends');
  if (!res.ok) throw new Error('Failed to load friends');
  return res.json(); // { friends, incoming, outgoing }
}

export async function searchUsers(query) {
  const res = await authFetch(`/api/friends/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error('Search failed');
  const data = await res.json();
  return data.users;
}

export async function sendFriendRequest(username) {
  const res = await authFetch('/api/friends/request', {
    method: 'POST',
    body: JSON.stringify({ username })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to send request');
  return data; // { friendshipId, status: 'pending' | 'accepted', user }
}

export async function acceptFriendRequest(friendshipId) {
  const res = await authFetch(`/api/friends/${friendshipId}/accept`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to accept request');
  return res.json();
}

export async function removeFriendship(friendshipId) {
  const res = await authFetch(`/api/friends/${friendshipId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to remove friendship');
  return res.json();
}

// ---- Matches & friend browsing ----

export async function getMatches() {
  const res = await authFetch('/api/trades/matches');
  if (!res.ok) throw new Error('Failed to load trade matches');
  const data = await res.json();
  return data.matches;
}

export async function getFriendWishlist(userId) {
  const res = await authFetch(`/api/friends/${userId}/wishlist`);
  if (!res.ok) throw new Error("Failed to load friend's wishlist");
  const data = await res.json();
  return data.items;
}

export async function getFriendTradeables(userId) {
  const res = await authFetch(`/api/friends/${userId}/tradeables`);
  if (!res.ok) throw new Error("Failed to load friend's tradeables");
  const data = await res.json();
  return data.items;
}

// ---- Trade proposals ----

export async function createTrade(recipientId, items) {
  const res = await authFetch('/api/trades', {
    method: 'POST',
    body: JSON.stringify({ recipientId, items })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create trade proposal');
  return data.trade;
}

export async function getTrades(status = 'active') {
  const res = await authFetch(`/api/trades?status=${status}`);
  if (!res.ok) throw new Error('Failed to load trades');
  const data = await res.json();
  return data.trades;
}

// action: 'accept' | 'decline' | 'cancel' | 'complete'
export async function actOnTrade(tradeId, action) {
  const res = await authFetch(`/api/trades/${tradeId}/${action}`, { method: 'POST' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Failed to ${action} trade`);
  return data;
}

// ---- Tradeables ----

export async function getTradeables() {
  const res = await authFetch('/api/tradeables');
  if (!res.ok) throw new Error('Failed to load tradeables');
  return res.json(); // { threshold, items, overrides }
}

export async function setTradeOverride(cardId, variant, mode, offerQty = null) {
  const res = await authFetch('/api/tradeables/override', {
    method: 'PUT',
    body: JSON.stringify({ cardId, variant, mode, offerQty })
  });
  if (!res.ok) throw new Error('Failed to save trade override');
  return res.json();
}

export async function removeTradeOverride(cardId, variant) {
  const res = await authFetch(
    `/api/tradeables/override/${encodeURIComponent(cardId)}/${encodeURIComponent(variant)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error('Failed to remove trade override');
  return res.json();
}
